const CACHE_KEY = 'redux_current_user';

export const CACHE_TIMEOUTS = {
  user: 24 * 60 * 60 * 1000, // 24 hours
  channels: 6 * 60 * 60 * 1000, // 6 hours
  streams: 6 * 60 * 60 * 1000, // 6 hours
  organizations: 12 * 60 * 60 * 1000, // 12 hours
};

/**
 * Clear user data cache
 */
export const clearUserCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

/**
 * Get cached user data from localStorage
 * @param {string} currentUserId - Current user ID to validate cache (optional)
 * @returns {Object|null} Cached user state or null if not found/invalid
 */
export const getCachedUserData = (currentUserId = null) => {
  try {
    const stored = localStorage.getItem(CACHE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    if (
      currentUserId &&
      parsed.cachedUserId &&
      parsed.cachedUserId !== currentUserId
    ) {
      clearUserCache();
      return null;
    }

    return parsed;
  } catch (error) {
    // Silently fail if localStorage is not available or data is corrupted
    return null;
  }
};

/**
 * Save user data to localStorage with timestamp
 * Only updates the specific data type, preserving other cached data
 * @param {Object} updatedData - Only the data being updated (e.g., { user: ... } or { channels: ..., channelsByRoles: ... })
 * @param {string} dataType - Type of data being updated ('user', 'channels', 'streams', 'organizations')
 */
export const saveUserDataToCache = (updatedData, dataType) => {
  try {
    const cached = getCachedUserData() || {};
    const now = Date.now();

    // Merge only the updated data with existing cache, preserving other data
    const stateToSave = {
      ...cached,
      ...updatedData,
      // Preserve cachedUserId if user data is not being updated
      cachedUserId: updatedData.user?.id || cached.cachedUserId,
      lastUpdated: {
        ...cached.lastUpdated,
        [dataType]: now,
      },
    };

    localStorage.setItem(CACHE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    // Silently fail if localStorage is not available
  }
};

/**
 * Check if cached data for a specific type is stale
 * @param {string} dataType - Type of data to check ('user', 'channels', 'streams', 'organizations')
 * @returns {boolean} True if data is stale or doesn't exist, false if fresh
 */
export const isDataStale = dataType => {
  const cached = getCachedUserData();
  if (!cached || !cached.lastUpdated) return true;

  const lastUpdated = cached.lastUpdated[dataType];
  if (!lastUpdated) return true;

  const timeout = CACHE_TIMEOUTS[dataType];
  if (!timeout) return true;

  const now = Date.now();
  return now - lastUpdated > timeout;
};

/**
 * Check which data types need to be fetched
 * @returns {Object} Object with flags for each data type indicating if it needs fetching
 */
export const getDataFetchFlags = () => ({
  user: isDataStale('user'),
  channels: isDataStale('channels'),
  streams: isDataStale('streams'),
  organizations: isDataStale('organizations'),
});
